import { createClient } from '@supabase/supabase-js';

// Initialize Supabase client with ANON key (not service key!)
const supabaseUrl = process.env.REACT_APP_SUPABASE_URL;
const supabaseAnonKey = process.env.REACT_APP_SUPABASE_ANON_KEY;

export const supabase = createClient(supabaseUrl, supabaseAnonKey);

/**
 * Format ISO date to European format (dd.mm.yyyy)
 * @param {string} isoDate - ISO format date (2025-01-25)
 * @returns {string} European format (25.01.2025)
 */
const formatDate = (isoDate) => {
  if (!isoDate) return '';
  
  const date = new Date(isoDate);
  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = date.getFullYear();
  
  return `${day}.${month}.${year}`;
};

/**
 * Transform image URL to frontend format
 * @param {string|null} imageUrl - Image URL from database
 * @returns {Object|null} Image object with src, alt, and id
 */
const transformImage = (imageUrl) => {
  if (!imageUrl) return null;
  
  return {
    src: imageUrl,
    alt: '', // Database doesn't store alt text
    id: 1 // Default id for question images
  };
};

/**
 * Load all tournaments with questions from Supabase
 * Transforms data to match frontend format exactly
 * @returns {Promise<Object>} Data in frontend format
 */
export const loadTournamentsData = async () => {
  try {
    
    // Fetch all packets (tournaments)
    const { data: packets, error: packetsError } = await supabase
      .from('packets')
      .select('*')
      .order('date', { ascending: false });

    if (packetsError) {
      console.error('Error loading packets:', packetsError);
      throw packetsError;
    }

    console.log(`Loaded ${packets?.length || 0} packets`);

    // For each packet, fetch authors and questions
    const packetsWithDetails = await Promise.all(
      packets.map(async (packet) => {
        // Fetch authors for this packet
        const { data: authors, error: authorsError } = await supabase
          .from('packet_authors')
          .select('author_name, question_count')
          .eq('packet_id', packet.id);

        if (authorsError) {
          console.error(`Error loading authors for packet ${packet.id}:`, authorsError);
        }

        // Fetch questions for this packet
        const { data: questions, error: questionsError } = await supabase
          .from('questions')
          .select('*')
          .eq('packet_id', packet.id)
          .order('question_number', { ascending: true });

        if (questionsError) {
          console.error(`Error loading questions for packet ${packet.id}:`, questionsError);
        }

        // Transform authors to frontend format: ["იოანე (12)", "მარიამ (8)"]
        const authorArray = authors?.map(a => 
          `${a.author_name} (${a.question_count})`
        ) || [];

        // Transform questions to frontend format
        const questionsData = questions?.map(q => ({
          id: q.question_number,
          text: q.text || '',
          answer: q.answer || '',
          author: q.author || '',
          comment: q.comment || '',
          source: q.source || '',
          url: q.url || '',
          image: transformImage(q.image)
        })) || [];

        // Return packet in frontend format
        return {
          id: packet.id,
          title: packet.title,
          difficulty: packet.difficulty || 'Mixed',
          date: formatDate(packet.date), // Convert to dd.mm.yyyy
          author: authorArray, // Array format
          questions: packet.questions_cnt || 0, // Rename field
          questions_data: questionsData // Array of questions
        };
      })
    );

    // Return in exact frontend format
    const result = {
      packets: packetsWithDetails
    };

    console.log('✓ Tournaments loaded and transformed successfully');
    console.log(`Total packets: ${packetsWithDetails.length}`);
    console.log(`Total questions: ${packetsWithDetails.reduce((sum, p) => sum + (p.questions || 0), 0)}`);

    return result;

  } catch (error) {
    console.error('Error in loadTournamentsData:', error);
    throw error;
  }
};

/**
 * Load single tournament by ID
 * @param {number} packetId - Tournament ID
 * @returns {Promise<Object>} Tournament data
 */
export const loadTournamentById = async (packetId) => {
  try {
    // Fetch packet
    const { data: packet, error: packetError } = await supabase
      .from('packets')
      .select('*')
      .eq('id', packetId)
      .single();

    if (packetError) throw packetError;

    // Fetch authors
    const { data: authors } = await supabase
      .from('packet_authors')
      .select('author_name, question_count')
      .eq('packet_id', packetId);

    // Fetch questions
    const { data: questions } = await supabase
      .from('questions')
      .select('*')
      .eq('packet_id', packetId)
      .order('question_number', { ascending: true });

    // Transform to frontend format
    const authorArray = authors?.map(a => 
      `${a.author_name} (${a.question_count})`
    ) || [];

    const questionsData = questions?.map(q => ({
      id: q.question_number,
      text: q.text || '',
      answer: q.answer || '',
      author: q.author || '',
      comment: q.comment || '',
      source: q.source || '',
      url: q.url || '',
      image: transformImage(q.image)
    })) || [];

    return {
      id: packet.id,
      title: packet.title,
      difficulty: packet.difficulty || 'Mixed',
      date: formatDate(packet.date),
      author: authorArray,
      questions: packet.questions_cnt || 0,
      questions_data: questionsData
    };

  } catch (error) {
    console.error('Error loading tournament:', error);
    throw error;
  }
};

export default {
  loadTournamentsData,
  loadTournamentById
};